import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useClassroomContext } from 'contexts/ClassroomContext';
import { useAssignmentContext } from 'contexts/AssignmentContext';
import Book from 'models/Book';
import studentService from 'services/studentService';
import MissedWord from 'models/MissedWord';
import Modal from 'view/components/common/Modal';
import {
  QuestionViewerState,
  QuestionsViewer,
} from 'view/components/student/QuestionsViewer';
import StudentQuestion from 'models/StudentQuestion';
import QuestionResponse from 'models/QuestionResponse';
import { AppPage } from 'view/components/common/AppPage';
import useApiCall from 'contexts/ApiCall';
import StudentGradebook from 'models/StudentGradebook';
import StudentGradebookEntry from 'models/StudentGradebookEntry';
import CachingEngine from 'utils/CachingEngine';
import ReadingUtils from 'utils/ReadingUtils';
import AssignmentSettings from 'view/components/student/assignment/AssignmentSettings';
import Question from 'models/Question';
import AssignmentContent from 'view/components/student/assignment/AssignmentContent';
import { calculateKeyWordAccuracy } from 'utils/assignmentUtils';
import { QuestionType } from 'utils/types';
import { EvaluationResult } from 'utils/speakingAssessmentUtils';
import AssignmentViewerSidebar from 'view/components/student/assignment/AssignmentViewerSidebar';
import teacherService from 'services/teacherService';
import { useAssignmentFeedbackContext } from 'contexts/AssignmentFeedbackContext';

import 'view/style/student/assignment.css';

export const AssignmentSubmissionPage: React.FC = () => {
  const { classroom } = useClassroomContext();
  const { assignment, setAssignment } = useAssignmentContext();
  const { classroomId, assignmentId } = useParams();
  const [book, setBook] = useState<Book>();
  const { feedback, setFeedback } = useAssignmentFeedbackContext();
  const [isLoadingFeedback, setIsLoadingFeedback] = useState<boolean>(false);
  const [missedWords, setMissedWords] = useState<MissedWord[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [questions, setQuestions] = useState<StudentQuestion[]>();
  const [viewQuestions, setViewQuestions] = useState<boolean>(false);
  const [isReading, setIsReading] = useState<boolean>(false);
  const [isOverdue, setIsOverdue] = useState<boolean>(false);
  const [useDyslexic, setUseDyslexic] = useState<boolean>(false);
  const [viewSettings, setViewSettings] = useState<boolean>(false);
  const [activeQuestion, setActiveQuestion] = useState<StudentQuestion>();
  const makeApiCall = useApiCall();
  const navigate = useNavigate();

  // Load feedback
  useEffect(() => {
    if (
      !assignment?.submission?.id ||
      isLoadingFeedback ||
      (feedback && feedback.length)
    )
      return;
    setIsLoadingFeedback(true);
    makeApiCall(teacherService.getAssignmentFeedback, assignment.submission.id)
      .then((feedbackData) => {
        setFeedback(feedbackData);
        setIsLoadingFeedback(false);
      })
      .catch((error) => {
        console.error('Error loading feedback:', error);
        setIsLoadingFeedback(false);
      });

    // eslint-disable-next-line
  }, [
    assignment?.submission?.id,
    setFeedback,
    setIsLoadingFeedback,
    makeApiCall,
  ]);

  // Load assignment data once
  useEffect(() => {
    if (!classroom || !assignmentId || book || questions) return;

    console.debug('ASSIGNMENT: Loading assignment data');

    // Create a flag to prevent multiple API calls
    const controller = new AbortController();

    makeApiCall(studentService.getAssignmentById, assignmentId, assignment)
      .then((respAssignment) => {
        if (controller.signal.aborted) return;

        console.debug('ASSIGNMENT: Assignment data loaded successfully');
        setAssignment(respAssignment.assignment);
        setBook(respAssignment.book);
        setQuestions(respAssignment.questions);
        setIsOverdue(
          new Date(respAssignment.assignment.assignment.due_date) < new Date(),
        );
      })
      .catch((error) => {
        if (controller.signal.aborted) return;
        console.error('ASSIGNMENT: Error loading assignment', error);
        navigate(-1);
      });

    return () => {
      controller.abort();
    };
    // eslint-disable-next-line
  }, [
    // IMPORTANT: Only include dependencies that won't change as a result of this effect
    // Remove assignment, book, questions from dependencies to prevent loops
    classroom,
    classroomId,
    assignmentId,
    navigate,
    makeApiCall,
  ]);

  // Load missed words once when assignment changes
  useEffect(() => {
    // Only run this effect when:
    // 1. We have an assignment
    // 2. We don't already have missed words
    // 3. We are in loading state
    if (missedWords.length > 0 || !assignment || !isLoading) return;

    console.debug('ASSIGNMENT: Loading missed words');

    // Create a flag to prevent multiple API calls
    const controller = new AbortController();

    // Set loading state
    setIsLoading(true);

    if (
      assignment.assignment.assignment_type === 'Flashcards' ||
      assignment.assignment.assignment_type === 'Pronounce'
    ) {
      if (assignment.assignment.key_words.length) {
        // Use key words directly if available
        const words = assignment.assignment.key_words.map(
          (kw) => new MissedWord('-1', kw, '-1', 0, false, true),
        );
        setMissedWords(words);
        setIsLoading(false);
      } else {
        // Fetch student missed words
        makeApiCall(
          studentService.getStudentMissedWords,
          assignment?.submission.student_profile,
        )
          .then((respWords) => {
            if (controller.signal.aborted) return;

            console.debug('ASSIGNMENT: Student missed words loaded');
            setMissedWords(respWords.slice(0, assignment.assignment.length));
          })
          .catch((error) => {
            if (controller.signal.aborted) return;
            alert(error.message);
          })
          .finally(() => {
            if (!controller.signal.aborted) {
              setIsLoading(false);
            }
          });
      }
    } else {
      // Fetch assignment missed words
      makeApiCall(
        studentService.getAssignmentMissedWords,
        assignment.submission,
      )
        .then((respWords) => {
          if (controller.signal.aborted) return;

          console.debug('ASSIGNMENT: Assignment missed words loaded');
          setMissedWords(respWords);
        })
        .catch((error) => {
          if (controller.signal.aborted) return;
          alert(error.message);
        })
        .finally(() => {
          if (!controller.signal.aborted) {
            setIsLoading(false);
          }
        });
    }

    return () => {
      controller.abort();
    };
    // eslint-disable-next-line
  }, [
    // IMPORTANT: Only depend on assignment ID, not the entire assignment object
    // This prevents re-fetching when other parts of assignment are updated
    assignment?.submission?.id,
    isLoading,
    makeApiCall,
    // Depend on missedWords.length to prevent re-fetching when we already have words
    missedWords.length,
  ]);

  const handleExitAssignment = () => {
    questions?.map((q) =>
      CachingEngine.deleteData(`${q.question.index}-audio`),
    );
    setAssignment(null);
    navigate(-1);
  };

  const handleBookCompleted = (
    elapsedTime: number = 0,
    response?: EvaluationResult[],
  ) => {
    if (!assignment || !book || assignment.submission.completion_score === 100)
      return;
    const numWords = book.getWords().length;
    const updatedAssignment = StudentGradebookEntry.fromServerEntry({
      assignment: assignment.assignment.toJSON(),
      submission: {
        ...assignment.submission.toJSON(),
        grade: 100,
        completion_score: 100,
        correctness_score: Math.max(
          Math.round((1 - missedWords.length / numWords) * 100),
          0,
        ),
        key_word_accuracy_score: calculateKeyWordAccuracy(
          missedWords,
          assignment.assignment.key_words,
          book.getWords(),
        ),
        date_completed: new Date(Date.now()).toJSON(),
        word_index: numWords,
        completion_time: elapsedTime,
        response: response,
      },
    });
    if (!updatedAssignment) return;
    setAssignment(updatedAssignment);
    makeApiCall(
      studentService.updateAssignmentSubmission,
      updatedAssignment.submission,
    ).catch((error) => alert(error.message));
    updateCachedGradebook(updatedAssignment);
  };

  const handleStopReading = (
    index: number,
    elapsedTime: number = 0,
    response?: EvaluationResult[],
  ) => {
    console.debug(`saving at index: ${index}`);
    if (!assignment || !book || index === assignment.submission.word_index)
      return;
    const numWords = book.getWords().length;
    const updatedAssignment = StudentGradebookEntry.fromServerEntry({
      assignment: assignment.assignment.toJSON(),
      submission: {
        ...assignment.submission.toJSON(),
        grade: Math.round((index / numWords) * 100),
        completion_score: Math.round((index / numWords) * 100),
        correctness_score: Math.max(
          Math.round((1 - missedWords.length / index) * 100),
          0,
        ),
        key_word_accuracy_score: 0,
        word_index: index,
        completion_time: elapsedTime,
        response: response,
      },
    });
    if (!updatedAssignment) return;
    setAssignment(updatedAssignment);
    studentService
      .updateAssignmentSubmission(updatedAssignment.submission)
      .catch((error) => alert(error.message));
    updateCachedGradebook(updatedAssignment);
  };

  const handleMissedWord = async (
    word: string,
    index: number,
    known: boolean,
    question?: Question,
  ) => {
    if (
      !assignment ||
      missedWords.findIndex(
        (mw) => mw.word === word && mw.word_index === index,
      ) !== -1
    )
      return;

    studentService
      .createMissedWord(
        ReadingUtils.stripSpecialCharsFromEndsOfString(word),
        assignment.submission.getId(),
        index,
        known,
        assignment.assignment.key_words.includes(word),
        question?.id,
      )
      .then((respWord) =>
        setMissedWords((prevWords) => [...prevWords, respWord]),
      )
      .catch((error) => alert(error.message));
  };

  const handleCloseQuestions = () => {
    setViewQuestions(false);
  };

  const handleViewQuestions = () => {
    setViewQuestions(true);
  };

  const handleQuestionResponseChanged = (
    question: Question,
    response: QuestionResponse,
  ) => {
    console.log(question, response);
    if (
      !questions ||
      ((response.response === '' || !response.response) &&
        (!response.choice || response.choice === '') &&
        question.question_type !== QuestionType.READALOUD)
    )
      return;

    const updatedQuestion = new StudentQuestion(
      Question.fromQuestion(question),
      QuestionResponse.fromQuestionResponse(response),
    );

    // Make the API call to save the updated response
    makeApiCall(
      studentService.updateQuestionResponse,
      updatedQuestion.response,
      question.question_type === QuestionType.WRITING
        ? book?.getText()
        : undefined,
    )
      .then((resp) => {
        // Update the questions array immutably, preserving order
        setQuestions((prevQuestions) =>
          prevQuestions!.map((q) =>
            q.question.id === question.id ? updatedQuestion : q,
          ),
        );
      })
      .catch((error) => alert(error.message));

    // Return the updated question object
    return updatedQuestion;
  };

  const handleListeningPaused = (timeStamp: number, duration: number) => {
    console.debug('paused', timeStamp, duration);
    if (!assignment || !book || timeStamp === assignment.submission.time_stamp)
      return;
    const completionScore = (timeStamp / duration) * 100;
    const updatedAssignment = StudentGradebookEntry.fromServerEntry({
      assignment: { ...assignment.assignment },
      submission: {
        ...assignment.submission,
        grade: completionScore,
        completion_score: completionScore,
        correctness_score: 100,
        key_word_accuracy_score: 100,
        time_stamp: timeStamp,
        date_completed:
          completionScore === 100 ? new Date(Date.now()).toJSON() : undefined,
      },
    });
    if (!updatedAssignment) return;
    setAssignment(updatedAssignment);
    studentService
      .updateAssignmentSubmission(updatedAssignment.submission)
      .catch((error) => alert(error.message));
    updateCachedGradebook(updatedAssignment);
  };

  const handleListeningCompleted = () => {
    console.debug('listening completed!');
    if (!assignment || 100 === assignment.submission.completion_score) return;
    const updatedAssignment = StudentGradebookEntry.fromServerEntry({
      assignment: { ...assignment.assignment },
      submission: {
        ...assignment.submission,
        grade: 100,
        completion_score: 100,
        correctness_score: 100,
        key_word_accuracy_score: 100,
        date_completed: new Date(Date.now()).toJSON(),
      },
    });
    if (!updatedAssignment) return;
    setAssignment(updatedAssignment);
    studentService
      .updateAssignmentSubmission(updatedAssignment.submission)
      .catch((error) => alert(error.message));
    updateCachedGradebook(updatedAssignment);
  };

  const handleMultiPartCompleted = () => {
    console.debug('multi part completed!');
    if (!assignment || 100 === assignment.submission.completion_score) return;
    const updatedAssignment = StudentGradebookEntry.fromServerEntry({
      assignment: { ...assignment.assignment },
      submission: {
        ...assignment.submission,
        grade: 100,
        completion_score: 100,
        correctness_score: questions
          ? Math.round(
              questions.reduce((sum, item) => sum + item.response.grade, 0) /
                questions.length,
            )
          : 100,
        key_word_accuracy_score: 100,
        date_completed: new Date(Date.now()).toJSON(),
      },
    });
    if (!updatedAssignment) return;
    setAssignment(updatedAssignment);
    studentService
      .updateAssignmentSubmission(updatedAssignment.submission)
      .catch((error) => alert(error.message));
    updateCachedGradebook(updatedAssignment);
  };

  const updateCachedGradebook = async (entry: StudentGradebookEntry) => {
    // get the cached gradebook
    const cachedGradebook = await CachingEngine.getData(
      `${classroom?.getId()}-gradebook`,
    );
    if (!cachedGradebook) return;
    const gradebook =
      StudentGradebook.fromServerStudentGradebook(cachedGradebook).updateEntry(
        entry,
      );
    await CachingEngine.setData(`${classroom?.getId()}-gradebook`, gradebook);
  };

  const handleWritingResponseCompleted = (completed = true) => {
    console.debug('writing completed!');
    if (!assignment) return;
    const updatedAssignment = StudentGradebookEntry.fromServerEntry({
      assignment: { ...assignment.assignment },
      submission: {
        ...assignment.submission,
        grade: 0,
        completion_score: 0,
        correctness_score: 0,
        key_word_accuracy_score: 0,
        date_completed: new Date(Date.now()).toJSON(),
      },
    });
    if (!updatedAssignment) return;
    setAssignment(updatedAssignment);
    studentService
      .updateAssignmentSubmission(updatedAssignment.submission)
      .then((resp) => {
        setFeedback(resp.feedback);
      })
      .catch((error) => alert(error.message));
    updateCachedGradebook(updatedAssignment);
  };

  if (!assignment) return <AppPage />;

  return (
    <AppPage>
      <div className="app-page-content">
        {!isReading && (
          <AssignmentViewerSidebar
            assignment={assignment}
            missedWords={missedWords}
            useDyslexic={useDyslexic}
            onExitAssignment={handleExitAssignment}
            onViewSettings={() => setViewSettings(true)}
            onToggleDyslexic={() => setUseDyslexic(false)}
            activeQuestion={activeQuestion}
          />
        )}
        <AssignmentContent
          assignment={assignment}
          book={book}
          missedWords={missedWords}
          questions={questions}
          isReading={isReading}
          useDyslexic={useDyslexic}
          setIsReading={setIsReading}
          onBookCompleted={handleBookCompleted}
          onStopReading={handleStopReading}
          onMissedWord={handleMissedWord}
          onViewQuestions={handleViewQuestions}
          onListeningCompleted={handleListeningCompleted}
          onListeningPaused={handleListeningPaused}
          onMultiPartCompleted={handleMultiPartCompleted}
          onQuestionResponseChanged={handleQuestionResponseChanged}
          onWritingAssignmentCompleted={handleWritingResponseCompleted}
          onAddFeedback={() => {}}
          setActiveQuestion={setActiveQuestion}
        />
        {questions && (
          <Modal isOpen={viewQuestions} onClose={handleCloseQuestions}>
            <QuestionsViewer
              state={
                !isOverdue
                  ? QuestionViewerState.InProgress
                  : assignment?.submission.questions_grade
                  ? QuestionViewerState.Graded
                  : QuestionViewerState.Locked
              }
              grade={assignment?.submission.questions_grade}
              questions={questions}
              onQuestionChange={handleQuestionResponseChanged}
              onExit={handleCloseQuestions}
            />
          </Modal>
        )}
        <Modal isOpen={viewSettings} onClose={() => setViewSettings(false)}>
          <AssignmentSettings />
        </Modal>
      </div>
    </AppPage>
  );
};
